import { Agency, AgencyCategory, AgencyAbbr, JogId } from "../../../../reducers/states/agency";
import { Button, DialogContentText, Divider, MenuItem } from "@mui/material";
import { FC, useState, useEffect, useImperativeHandle, useCallback } from "react";
import { useAuthContext } from "../../../../core";
import { ValidatePhoneNumberUtil, ValidateEmailUtil, CleanPhoneNumberUtil } from "../../../../utils/string-utils";
import ViewEditSelect from "../../../common/view-edit-select";
import ViewEditSelectJog from "../../../common/view-edit-select-jog";
import ViewEditTextField from "../../../common/view-edit-text-field";
import React from "react";
import { useAgencies } from "../../../../hooks/agencies";
import { useSearchParams } from "react-router-dom";
import { IsMockDisabled } from "../../../../utils/user-info-utils";
import { UserGroups } from "../../../../enums/user-groups";

export enum AgencyFormMode {
  CREATE = "CREATE",
  EDIT = "EDIT",
  READ_ONLY = "READ_ONLY", // View mode but cannot switch to Edit mode
  VIEW = "VIEW", // View mode and can switch to Edit mode
}

export interface AgencyFormHandle {
  getValues: () => Agency,
  reset: () => void,
}

interface AgencyFormProps {
  initialValue?: Agency, // for all modes but CREATE
  mode: AgencyFormMode,
  onValidityChanged?: (isValid: boolean) => void,
}

const AgencyForm = React.forwardRef<AgencyFormHandle, AgencyFormProps>((props, ref) => {
  const { authState } = useAuthContext();
  const agencies = useAgencies();
  const [searchParams] = useSearchParams();

  // UI controls
  const [isEdit, setIsEdit] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [mode, setMode] = useState(AgencyFormMode.VIEW);

  // Agency fields UI input values
  const [agencyInput, setAgencyInput] = useState<Agency>({} as Agency);

  const updateValidity = useCallback((newIsValid: boolean): void => {
    if (isValid !== newIsValid) {
      setIsValid(newIsValid);
      props?.onValidityChanged?.(newIsValid);
    }
  }, [isValid, props]);

  useImperativeHandle(ref, () => ({
    getValues: () => {
      // commented fields while it's TBD if we add this feature
      // let cleanNumber = agencyInput.primaryContactPhoneNumber;
      // if (cleanNumber) {
      //   cleanNumber = CleanPhoneNumberUtil(cleanNumber);
      // }

      // const agency = {
      //   ...agencyInput,
      //   primaryContactPhoneNumber: cleanNumber
      // };

      console.info("[INFO] handleSubmitAndClose() agency:", agencyInput);
      return agencyInput;
    },

    reset: () => {
      setAgencyInput({} as Agency);
      setMode(props.mode);
      updateValidity(false);
    }
  }));

  const isAbbreviationAlreadyUsed = useCallback((): boolean => {
    return !!agencies.find(
      agency => agency.abbreviation.localeCompare(agencyInput.abbreviation, undefined, { sensitivity: "base" }) === 0 &&
      agency.id !== agencyInput.id);
  }, [agencies, agencyInput.abbreviation, agencyInput.id])

  useEffect(() => {
    if (props.mode !== AgencyFormMode.CREATE && props.initialValue) {
      setAgencyInput(props.initialValue ? { ...props.initialValue } : {} as Agency);
    }

    // The route to the page is protected, but doing this for extra safety
    const isSiteAdmin = authState.userGroups.indexOf(UserGroups.SITE_ADMIN) > -1;
    const actualMode = isSiteAdmin ? props.mode : AgencyFormMode.READ_ONLY;

    if(mode !== actualMode) {
      setMode(actualMode);
      setIsEdit(actualMode === AgencyFormMode.CREATE || actualMode === AgencyFormMode.EDIT);
    }
    
  }, [props.mode, props.initialValue, mode, authState.userGroups])

  const handleInputChange = (value: any, field: string) => {
    const isFieldBool = (field: string): boolean => {
      switch (field) {
        case "isFilter":
        case "isUserAgency":
          return true;
        default:
          return false;
      }
    }

    if (field === "jogIds") {
      const ids = value as string[];
      setAgencyInput((current) => ({
        ...current,
        jogIds: ids,
      }));
      return;
    }

    const input: string = value as string;

    // Note: UI/UX (and this function) should handle setting state with values for name/phone/email OR userId, not both.
    if (isFieldBool(field)) {
      setAgencyInput((current) => ({ ...current, [field]: value === "true" })); // set boolean value
    } else {
      setAgencyInput((current) => ({ ...current, [field]: input }));
    }
  } 

  useEffect(() => {
    // todo: jcavner: If edit mode, disable if no values have changed?

    // Disable submission if phone number is invalid. Blank and undefined are ok.
    // if (agencyInput.primaryContactPhoneNumber && !ValidatePhoneNumberUtil(agencyInput.primaryContactPhoneNumber)) {
    //   updateValidity(false);
    //   return;
    // }

    // Disable submission if email invalid. Blank and undefined are ok.
    // if (agencyInput.primaryContactEmail && !ValidateEmailUtil(agencyInput.primaryContactEmail)) {
    //   updateValidity(false);
    //   return;
    // }

    // Disable submission if primary contact userId has value and any primary contact fields are populated.
    // todo: jcavner: Create separate fields to display values from user info if userId is populated.    
    // if (agencyInput.primaryContactUserId && (agencyInput.primaryContactName || agencyInput.primaryContactEmail || agencyInput.primaryContactPhoneNumber)) {
    //   updateValidity(false);
    //   return;
    // }

    // Disable submission if any required field is blank
    const isDisabled = (
      !agencyInput.abbreviation ||
      !agencyInput.category ||
      (mode === AgencyFormMode.EDIT && !agencyInput.createdBy) ||
      (mode === AgencyFormMode.EDIT && !agencyInput.createdDate) ||
      !agencyInput.jogIds || agencyInput.jogIds.length < 1 ||
      !agencyInput.name ||
      !agencyInput.shortName
    );

    // After all other checks, ensure that abbreviation has not already been used for a different agency
    if(!isDisabled && isAbbreviationAlreadyUsed()) {
      updateValidity(false);
      return;
    }

    updateValidity(!isDisabled);
  }, [agencies, agencyInput, isAbbreviationAlreadyUsed, mode, updateValidity]);

  // todo: jcavner: Possibly delete this testing functionality in the future.
  const MockInputButton: FC = () => {
    return IsMockDisabled(authState.email, searchParams) || !(mode === AgencyFormMode.CREATE || mode === AgencyFormMode.EDIT) ? null : (
      <Button onClick={() => {
        setAgencyInput((current) => {
          // Set the next unique mock abbreviation value
          const firstAbbr = agencies.sort((a, b) => a.abbreviation > b.abbreviation ? -1 : 1).find(a => a.abbreviation.startsWith("AB"))?.abbreviation || agencies[0].abbreviation;
          let numPart = Number.parseInt(firstAbbr.match(/(\d+)$/)?.[1] ?? "0");
          const abbr = "AB" + ++numPart; // will work up to 999 since there's a 5 char abbr limit.

          return {
            ...current,
            // Note: add an ID here if you want to test a PUT?
            name: "a test name " + (new Date()).toISOString(),
            shortName: "test short name",
            abbreviation: abbr as AgencyAbbr,
            category: AgencyCategory.Other,
            isActive: true,
            isFilter: false,
            isUserAgency: true,
            jogIds: [JogId.SeattleArea],
          }
        });
      }} color="inherit" variant="contained">
        Mock Fill
      </Button>
    );
  };

  return (
    <>
      <MockInputButton />
      <ViewEditTextField
        props={{
          id: "name",
          label: "Agency Name",
          isEdit,
          initialValue: agencyInput.name,
          required: true,
          size: "medium",
          tooltip: "The full name of the agency.",
          handleInputChange: handleInputChange
        }}
      />
      <ViewEditTextField
        props={{
          characterLimit: 20,
          id: "shortName",
          label: "Agency Short Name",
          isEdit,
          initialValue: agencyInput.shortName,
          // todo: jcavner: I want to render this tooltip with common abbreviations in a list (e.g., "Dept.", "Co", "FD", "PD")
          required: true,
          size: "medium",
          tooltip: `A short name to display in the app where space is limited. Common abbreviations for parts of the full name are, "PD" for Police Dept., "FD" for Fire District or Fire Dept., "Co." for County, "DOT" for Department of Transportation (e.g., "Seattle DOT"), "SD" for School District, or reusing the abbreviation (e.g., WSDOT or WSP)`,
          handleInputChange: handleInputChange
        }}
      />
      <ViewEditTextField
        props={{
          characterLimit: 5,
          error: isAbbreviationAlreadyUsed(),
          helperText: isAbbreviationAlreadyUsed() ? "This abbrevation is already used" : "",
          id: "abbreviation",
          label: "Abbreviation",
          isEdit,
          initialValue: agencyInput.abbreviation,
          required: true,
          size: "medium",
          tooltip: "An abbreviation to display in the app where space is very limited, and for dispatch event display and filtering, when applicable.",
          handleInputChange: handleInputChange
        }}
      />
      <ViewEditSelect
        props={{
          id: "category",
          isEdit,
          label: "Category",
          required: true,
          size: "medium",
          value: agencyInput.category,
          handleInputChange: handleInputChange,
        }}
      >
        {Object.values(AgencyCategory).map((type) => {
          return <MenuItem key={type} value={type}>{type}</MenuItem>;
        })}
      </ViewEditSelect>
      <ViewEditSelectJog
        id="jogIds"
        isEdit={isEdit}
        label="Associated JOG(s)"
        //maxSelectedCount={MaxSelectedJogCount}
        multiple
        required
        size="medium"
        tooltip="Select the JOG(s) this Agency is part of."
        //userPrimaryJogId={jogId}
        value={agencyInput.jogIds || [] as string[]}
        handleInputChange={handleInputChange}
      />
      <ViewEditSelect
        props={{
          id: "isUserAgency",
          label: "Allow inviting VCC users from this agency?",
          value: agencyInput.isUserAgency?.toString() ?? true.toString(),
          isEdit,
          required: true,
          size: "medium",
          // todo: jcavner: use info icon instead of tooltip?
          tooltip: `Set to YES if you're ready for users from this agency to be invited to the VCC. This can also be NO initially and set to YES later (e.g., to add the Agency, but wait to allow inviting users). Or, sometimes remain NO and never switch to YES (e.g., for Agencies we've added for CAD feeds, but won't have users from)`,
          handleInputChange: handleInputChange,
        }}
      >
        <MenuItem key="Yes" value={true.toString()}>
          Yes
        </MenuItem>
        <MenuItem key="No" value={false.toString()}>
          No
        </MenuItem>
      </ViewEditSelect>
      {/* // todo: jcavner: Decide if this should even be an input here. Perhaps not. Or, only allowed when editing? E.g., agency is added first for invites. After CAD is integrated, it's edited to set this to yes?? */}
      <ViewEditSelect
        props={{
          id: "isFilter",
          label: "VCC currently ingests a CAD feed for this Agency?",
          value: agencyInput.isFilter?.toString() ?? false.toString(),
          isEdit: isEdit,
          required: true,
          size: "medium",
          // todo: jcavner: use info icon instead of tooltip?
          tooltip: "This should only be set to YES after an engineer has finished implementing ingestion of a CAD feed for this agency. Usually, an engineer will change this to YES when ready.",
          handleInputChange: handleInputChange,
        }}
      >
        <MenuItem key="Yes" value={true.toString()}>
          Yes
        </MenuItem>
        <MenuItem key="No" value={false.toString()}>
          No
        </MenuItem>
      </ViewEditSelect>
      {/* Primary contact may a future feature. Comment out for now. Probably can delete if we don't work on this by early 2027. */}
      {/* <>
        <Divider sx={{ margin: "1em 0" }} />
        <DialogContentText>
          Enter primary contact information for this Agency if available
        </DialogContentText>
        <ViewEditTextField
          props={{
            id: "primaryContactName",
            initialValue: agencyInput.primaryContactName,
            isEdit,
            label: "Primary Contact Name",
            size: "medium",
            handleInputChange: handleInputChange
          }}
        />
        <ViewEditTextField
          props={{
            id: "primaryContactEmail",
            initialValue: agencyInput.primaryContactEmail,
            isEdit,
            label: "Primary Contact Email",
            size: "medium",
            handleInputChange: handleInputChange
          }}
        />
        <ViewEditTextField
          props={{
            id: "primaryContactPhoneNumber",
            initialValue: agencyInput.primaryContactPhoneNumber,
            isEdit,
            label: "Primary Contact Phone Number",
            size: "medium",
            handleInputChange: handleInputChange
          }}
        />
      </> */}
    </>
  );
});

export default AgencyForm;